import { Component, Input, OnInit, OnDestroy, Inject } from '@angular/core';
import { firstValueFrom, interval, takeUntil, Subject, Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';

import { NameService } from '../../services/name/name.service';
import { DolbyService } from '../../services/dolby/dolby.service';
import { UserService } from '../../services/user/user.service';
import { AnalyticsService } from '../../services/analytics/analytics.service';
import { SupportCenterService } from '../../services/support-center/support-center.service';
import { ThemeService } from '../../services/theme/theme.service';
import { ConfTypes, UserModel } from '@sc/types';
import { WebhookEvent, WebhookEventNames } from '@sc/types';
import { Session, ParticipantFsDolby, Locations, Prompt, PromptTypes } from '@sc/types';
import { SessionsService } from '../../services/sessions/sessions.service';
import { Roles } from '@sc/types';
import { WalletService } from '../../services/wallet/wallet.service';
import * as dayjs from 'dayjs';
import * as dayjsIsToday from 'dayjs/plugin/isToday';
import * as dayjsIsTomorrow from 'dayjs/plugin/isTomorrow';
import * as relativeTime from 'dayjs/plugin/relativeTime';
import * as weekday from 'dayjs/plugin/weekday';
import * as isBetween from 'dayjs/plugin/isBetween';
import * as utc from 'dayjs/plugin/utc';
import { GeneralToastComponent } from '../../toasts/general-toast/general-toast.component';
import { CalifoneService } from '../../services/califone/califone.service';
import * as Rollbar from 'rollbar';
import { RollbarService } from '../../services/rollbar/rollbar.service';
import { ColorsService } from '../../services/colors/colors.service';
import { EquipmentService } from '../../services/equipment/equipment.service';

@Component({
  selector: 'sc-backstage-access',
  templateUrl: 'backstage-access.component.html',
  styleUrls: ['backstage-access.component.scss'],
})
export class BackstageAccessComponent implements OnInit, OnDestroy {
  @Input() session: Session;
  @Input() guestEmail?: string;

  participant$ = this.sessionsService.participant$;
  activeUser$ = this.userService.activeUser$;
  studioSession$ = this.sessionsService.studioSession$;
  studioOrg$ = this.sessionsService.studioOrg$;
  plan$ = this.walletService.studioPlan$;

  participant: ParticipantFsDolby;
  mediaStream: MediaStream;
  user: UserModel;
  joining = false;
  name: string;
  pronouns: string;
  nameFirstChar: string;
  dolbyInitialized = false;
  retryDolbyInitialize = false;
  allowIntoTheSession = true; //FIXME: Bypass request access for user testing, fix to false
  requestedAccess = false;
  location: Locations;
  subs: Array<Subscription> = [];

  theme$ = this.themeService.theme$;

  elapsedSeconds: number;
  counterHasStarted: boolean;
  countdownHasFinished: boolean;
  remainingSeconds: number;
  timeFormatted: string;

  startCountdown$: Subscription;
  stopCountdown$ = new Subject();

  conversation: any;

  constructor(
    private colorsService: ColorsService,
    private equipmentService: EquipmentService,
    private toastrService: ToastrService,
    private userService: UserService,
    private nameService: NameService,
    private dolbyService: DolbyService,
    private califoneService: CalifoneService,
    private analyticsService: AnalyticsService,
    private supportCenterService: SupportCenterService,
    private themeService: ThemeService,
    private sessionsService: SessionsService,
    private walletService: WalletService,
    @Inject(RollbarService) private rollbar: Rollbar
  ) {}

  ngOnInit() {
    this.equipmentService.setupEquipment();
    this.setupUser();
    this.setupSession();
    this.setupParticipant();

    dayjs.extend(dayjsIsToday);
    dayjs.extend(dayjsIsTomorrow);
    dayjs.extend(relativeTime);
    dayjs.extend(weekday);
    dayjs.extend(utc);
    dayjs.extend(isBetween);
  }

  setupSession() {
    const sub = this.sessionsService.studioSession$.subscribe((session) => {
      this.session = session;
      if (this.session?.confType === ConfTypes.DAILY) {
        // Redirect the user back to the stage
        // if the session is a Daily Conference

        this.toastrService.warning(
          `This is a Daily Conference. We currently do not support backstage access for Daily Conferences. We will auto redirect you shortly...`,
          `Backstage not Supported`,
          {
            progressBar: true,
            progressAnimation: 'decreasing',
            closeButton: true,
            tapToDismiss: false,
            timeOut: 20 * 1000,
            toastComponent: GeneralToastComponent,
          }
        );
      }

      // add a 3 second timeout before redirecting the user back to the stage
      setTimeout(() => {
        window.location.href = `/studio/${this.session.showID}/session/${this.session.sessionID}`;
      }, 3000);
    });

    this.subs.push(sub);
  }

  setupParticipant() {
    const sub = this.participant$.subscribe((participant) => {
      if (!participant) return;
      this.participant = participant;
      this.location = participant.location;

      // FIXME: Commented out to bypass request access for user testing
      // if (this.location === Locations.BACKSTAGE) {
      //   this.allowIntoTheSession = true;
      // } else {
      //   this.allowIntoTheSession = false;
      //   this.promptForAccess();
      // }
    });
    this.subs.push(sub);
  }

  async setupLocation() {
    this.sessionsService
      .checkBackstageAccess(this.participant, this.sessionsService.studioSessionID$.value)
      .then((location) => {
        this.location = location;

        // FIXME: Commented out to bypass request access for user testing
        // if (this.location === Locations.BACKSTAGE) {
        //   this.allowIntoTheSession = true;
        // } else {
        //   this.promptForAccess();
        // }
      });
  }

  async requestAccess() {
    const name = await firstValueFrom(
      this.nameService.getName(this.studioSession$.value.sessionID, this.activeUser$.value.uid)
    );

    const prompt: Prompt = {
      title: `${name || 'Someone'} is requesting Backstage access`,
      message: `Do you want to invite ${name || 'them'} Backstage?`,
      creator: this.activeUser$.value.uid,
      presentAs: `toast`,
      type: PromptTypes.REQUESTBACKSTAGEACCESS,
      targetRole: Roles.SHOW_MANAGER,
    };

    try {
      await this.sessionsService.createSessionPrompt(prompt, this.sessionsService.studioSessionID$.value);
      this.toastrService.info(`Please wait for access to join`, `You requested backstage access`, {
        progressBar: true,
        progressAnimation: 'decreasing',
        closeButton: true,
        tapToDismiss: false,
        timeOut: 10 * 1000,
        toastComponent: GeneralToastComponent,
      });
      this.requestedAccess = true;
      this.analyticsService.track('requested backstage access', { name, success: true });
    } catch (error) {
      this.toastrService.error(
        `Failed to request backstage access. Please contact the host. ${error.message}`,
        `Failed to request backstage access`,
        {
          progressBar: true,
          progressAnimation: 'decreasing',
          closeButton: true,
          tapToDismiss: false,
          timeOut: 10 * 1000,
          toastComponent: GeneralToastComponent,
        }
      );

      this.analyticsService.track('requested backstage access', { name, success: false });
    }
  }

  promptForAccess() {
    // this.toastrService.error(`You can request to be let in`, `You were not allowed in the backstage.`, {
    //   progressBar: true,
    //   progressAnimation: 'decreasing',
    //   closeButton: true,
    //   tapToDismiss: false,
    //   timeOut: 10 * 1000,
    //   toastComponent: GeneralToastComponent,
    // });
  }

  async setupConvo() {
    await this.setupDolby();

    // await this.dolbyService.openSession({
    //   externalId: this.activeUser$.value.uid,
    //   name: this.activeUser$.value.fullName,
    //   avatarUrl: this.activeUser$.value.photoURL,
    // });

    await this.dolbyService.fetchConversation(this.dolbyService.conferenceAlias).then((convo) => {
      this.conversation = convo;
    });

    await this.setupLocation();
  }

  startEventCountDown() {
    this.startCountdown$ = interval(500)
      .pipe(takeUntil(this.stopCountdown$))
      .subscribe(() => {
        this.weeklyOfficeHoursCountDown();
      });
  }

  weeklyOfficeHoursCountDown() {
    // weekday 0 - Sunday | 1 - Monday | 2 - Tuesday | 3 - Wednesday | 4 - Thursday | 5 - Friday | 6 - Saturday
    const fridayDayOfWeek = dayjs().year(dayjs().year()).date(dayjs().date()).month(dayjs().month()).weekday(5);
    // Stop-Time until which the countdown gets decreased
    let stopTime = fridayDayOfWeek.hour(13).minute(30).second(0); // Friday 13:30
    const restarTime = stopTime.add(1, 'hour'); // Friday 14:30
    const now = dayjs();

    this.countdownHasFinished = now.isBetween(stopTime, restarTime);
    if (this.countdownHasFinished) {
      this.timeFormatted = '0:00';
      this.remainingSeconds = 0;
      this.stopCountdown$.next(true);
      this.stopCountdown$.complete();

      return;
    }

    if (now.isAfter(restarTime)) {
      stopTime = stopTime.add(1, 'week');
    }

    const daysDiff = stopTime.diff(now, 'days');
    let daysLeft;
    switch (daysDiff) {
      case 0:
        if (now.date() === stopTime.date()) {
          daysLeft = 0;
        } else {
          daysLeft = 7;
        }
        break;
      case -1:
        daysLeft = 6;
        break;
      case -2:
        daysLeft = 5;
        break;
      case -3:
        daysLeft = 4;
        break;
      case -4:
        daysLeft = 3;
        break;
      case -5:
        daysLeft = 2;
        break;
      case -6:
        daysLeft = 1;
        break;
      default:
        daysLeft = daysDiff;
    }

    let remainingTime = dayjs(stopTime.valueOf() - now.valueOf())
      .year(dayjs().year())
      .month(dayjs().month())
      .date(dayjs().date())
      .add(daysLeft, 'days');

    const hoursLeft = remainingTime.diff(now, 'hour');
    const minutesLeft = remainingTime.diff(now, 'minute');
    const secondsLeft = remainingTime.diff(now, 'second');
    this.remainingSeconds = stopTime.diff(now, 'second');

    remainingTime = remainingTime.subtract(now.utcOffset(), 'minute');
    const timeInHours = remainingTime.format('HH:mm:ss');

    const daysText = `${daysLeft ? daysLeft + ` Day` + (daysLeft !== 1 ? 's ' : ' ') : ''}`;
    const hoursText = `${
      !timeInHours.split(':')[0].match('00') && daysLeft !== 0
        ? timeInHours.split(':')[0] + ` Hour${hoursLeft !== 1 ? 's ' : ' '}`
        : ''
    }`;
    const minutesText = `${
      !timeInHours.split(':')[1].match('00')
        ? timeInHours.split(':')[1] + ` Minute${minutesLeft !== 1 ? 's ' : ' '}`
        : ''
    }`;
    const secondsText = timeInHours.split(':')[2] + ` Second${secondsLeft > 1 ? 's' : ''}`;

    const countDownText = daysText + hoursText + minutesText + secondsText;

    this.timeFormatted = countDownText;
    this.countdownHasFinished = now.isAfter(stopTime) && now.isBefore(restarTime);
  }

  async joinSession() {
    const locals = {
      convoOptions: null,
      convoID: null,
    };
    try {
      if (this.session?.communityEvent === true) {
        if (this.session?.activeCommunityEvent !== true) {
          return;
        } else if (this.countdownHasFinished !== true) {
          return;
        }
      }
      if (!this.allowIntoTheSession) {
        if (!this.requestedAccess) this.requestAccess();
        return;
      }

      if (this.equipmentService.hasPermission$.value) this.dolbyService.enumerateDevices();
      this.joining = true;
      this.analyticsService.track('clicked join session backstage');
      this.name = await this.nameService.getRandomNameIfEmpty(this.name);
      this.setName();

      await this.dolbyService.initSession();
      // locals.convoOptions = {
      //   alias: this.dolbyService.conferenceAlias,
      //   params: this.dolbyService.getConferenceParams(),
      // };
      // const convo = await this.dolbyService.createConversation(locals.convoOptions);
      // locals.convoID = convo.id;

      // if (!convo.isNew && !this.checkAvailability()) return;

      await this.dolbyService.joinBackstage();
      this.sessionsService.addParticipant(this.user.uid, this.studioSession$.value.sessionID);
      await this.colorsService.setParticipantColor(
        this.activeUser$.value.uid,
        this.colorsService.getRandomAccentColor()
      );

      const event: WebhookEvent = {
        name: WebhookEventNames.PARTICIPANT_JOIN,
        userName: this.name,
        orgID: this.studioOrg$.value.orgID,
        sessionID: this.session.sessionID,
        sessionTitle: this.session.sessionTitle,
        showID: this.session.showID,
        showName: this.session.showTitle,
        date: this.session.date,
        location: Locations.BACKSTAGE,
      };
      await this.califoneService.emitWebhookEvent(event, this.studioOrg$.value.orgID);

      this.analyticsService.track('joined session backstage', { success: true });
    } catch (error) {
      this.rollbar.error('Failed to Join Dolby Session', error, locals);
      this.toastrService.error(
        `This may be due to a network issue.  Check your connection, refresh the page, and try again.<br/><br/><code>${error.message}</code>`,
        `Failed to join the conversation`,
        {
          enableHtml: true,
          progressBar: false,
          closeButton: true,
          tapToDismiss: false,
          toastComponent: GeneralToastComponent,
          disableTimeOut: true,
        }
      );
      this.analyticsService.track('joined session backstage', { success: false });
    } finally {
      this.joining = false;
    }
  }

  async checkAvailability() {
    const availability = await this.dolbyService.checkSeatCountAvailability();
    if (!availability.backstageSeatAvailable) {
      this.joining = false;
      this.toastrService.info(
        `${this.walletService.studioPlan$.value.backstage} is the maximum for your ${this.walletService.studioPlan$.value.name} plan.` +
          `${
            this.walletService.studioPlan$.value.name.includes('studio')
              ? ''
              : ' If you need more seats please consider upgrading your plan!'
          }`,
        `${availability.backstageParticipantCount} Participants are already in the Backstage`,
        {
          progressBar: false,
          closeButton: true,
          tapToDismiss: false,
          disableTimeOut: true,
          toastComponent: GeneralToastComponent,
        }
      );
      return false;
    }
    return true;
  }

  async openSupportCenter() {
    this.supportCenterService.show();
    this.analyticsService.track('opened support center');
  }

  async setName() {
    const combinedName = this.pronouns ? this.name + ` (${this.pronouns})` : this.name;

    if (combinedName)
      await this.nameService.setName(this.sessionsService.studioSessionID$.value, this.user.uid, combinedName);
  }

  setupName() {
    const sub = this.nameService
      .getName(this.sessionsService.studioSessionID$.value, this.user.uid)
      .subscribe((name: string) => {
        if (!name) {
          return;
        }
        this.nameFirstChar = Array.from(name)[0];
        //const nameRegex = /^.+(?=\s\(.+(?=\)$))/; \\better, but incompatible with safari
        const nameRegex2 = /^.+\(/;
        const nameMatch = name.match(nameRegex2);
        //const pronounRegex = /(?<=\b\s\().+(?=\)$)/; \\better, but incompatible with safari
        const pronounRegex2 = /\(((\w|\d)+\/?(\w|\d)+)\)$/;
        this.name = nameMatch ? nameMatch[0].slice(0, nameMatch[0].length - 2) : name;
        const pronounsMatch = name.match(pronounRegex2);
        this.pronouns = pronounsMatch ? pronounsMatch[0].slice(1, pronounsMatch[0].length - 1) : '';
      });

    this.subs.push(sub);
  }

  async setupUser() {
    const sub = this.userService.activeUser$.subscribe((user) => {
      this.user = user;
    });
    this.subs.push(sub);

    await this.userService.activeUser$.toPromise();
    await this.sessionsService.studioSession$.nextExistingValue();
    await this.studioOrg$.nextExistingValue();
    this.setupName();
    this.setupConvo();

    // this.weeklyOfficeHoursCountDown();
    if (this.session?.communityEvent === true) {
      if (this.session?.countdownEnabled === true && this.session?.activeCommunityEvent === true) {
        this.startEventCountDown();
      } else if (this.session?.activeCommunityEvent !== true) {
        this.countdownHasFinished = false;
      } else {
        this.countdownHasFinished = true;
      }
    }

    this.analyticsService.track('entered backstage access', {
      email: this.user.email,
      id: this.sessionsService.studioSessionID$.value,
      castID: this.user.uid,
      when: new Date().toLocaleString(),
      title: this.session.sessionTitle,
    });
  }

  async setupDolby() {
    if (this.studioSession$.value?.confType === 'daily') return;

    if (this.dolbyService.sessionIsOpen()) {
      this.dolbyInitialized = true;
    } else this.dolbyInitialized = await this.dolbyService.initSession();
  }

  ngOnDestroy() {
    this.subs.forEach((sub) => {
      sub.unsubscribe();
    });
  }
}
